import React, { useId, useRef, useState, useMemo } from 'react';
import moment from 'moment';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
  ColumnFiltersState,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
} from '@tanstack/react-table';

import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { TruncatedText } from 'design-system/src/components/TruncatedText/TruncatedText';

import { createTrackingProps } from 'shared-utils/analytics';

import type { TaskSummary } from '../../api';
import { OutboundTaskTypeKey, PartnerTaskStatusFilter } from '../../api';
import { DRUG_CATEGORY_DROPDOWN_OPTIONS, MULTI_SELECT_DELINEATOR } from '../../constants';
import {
  sortOfficeNames,
  getDropdownInitialValueFromSearchParams,
  getInitialTableColumnFilterValueFromCookie,
  updateTableColumnFilterValueCookie,
  formatRemainingTime,
  getDropdownInitialValueFromSearchParamsAndCookie,
  tasksFilterValuesFromSearchParams,
  updateUrlSearchParams,
  getPrescriberFilterValue,
} from '../../utils';
import { stringsToDropdownOptions } from 'shared-utils/utils/dropdownFilter';
import { useFetchPortalTableFilters } from '../useFetchPortalTableFilters';
import { TableMultiselectFilter } from 'design-system/src/components/TableDropdownFilter/TableDropdownFilter';
import SaveFiltersModal from '../SaveFiltersModal/SaveFiltersModal';
import { SaveFiltersButton } from 'design-system/src/components/SaveFiltersButton/SaveFiltersButton';
import DateFilter from '../DateFilter';

import { areStringArraysEqual } from '../../utils';
import type {
  MultiselectAutocompleteOption,
  MultiselectAutocompleteHandle,
} from 'design-system/src/components/MultiselectAutocomplete/MultiselectAutocomplete';
import { AuthContext } from '../../auth';

import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ClickableRow,
  FilterBar,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  HeaderSortingIndicator,
  CenteredTableCell,
  TableTabList,
  TableResultCount,
  TabsAndSearchRow,
  FilterBarRow,
  SearchResultsBanner,
  DEFAULT_COLUMN_SIZE,
  DATE_COLUMN_SIZE,
  DateCellWithStatus,
  PreviewDateWithStatus,
} from '../Table';
import { TableSearchWithPreview } from '../TableSearchWithPreview';
import TableNullState, { TableNullStateType } from '../TableNullState/TableNullState';
import { useTableNullState } from '../../hooks/useTableNullState';
import FormattedCell from 'design-system/src/components/FormattedCell/FormattedCell';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { emptyTableCellCopy } from 'design-system/src/sharedCopy';
import { useDefaultFilters } from '../../hooks/useDefaultFilters';
import { PillStatus } from 'design-system/src/components/PillStatus';
import Button from 'design-system/src/components/Button/Button';
import SubmitAppealModal from './SubmitAppealModal';
import type { Patient, Drug } from '../../api';
import { useSearchTasks } from './useSearchTasks';
import { MIN_SEARCH_LENGTH } from '../../constants';
import { stripHtmlTags } from '../../utils';
import { SearchHighlightText } from '../SearchHighlightText/SearchHighlightText';

const columnHelper = createColumnHelper<TaskSummary>();

const TASKS_TABLE_MOBILE_BREAKPOINT = 1040;

// Columns that are always hidden (used for filtering only)
const HIDDEN_FILTER_COLUMNS = { drug_category: false, drug_name: false, office_name: false, task_type: false };

interface TaskTableProps {
  data: TaskSummary[];
  isLoading?: boolean;
  error?: Error | null;
}

/**
 * Preview item component for task search results.
 * Uses SearchHighlightText to highlight matching fields and truncate long text.
 */
const TaskPreviewItem: React.FC<{ task: TaskSummary; searchTerm: string }> = ({ task, searchTerm }) => {
  const messageText = (task.last_comment_message ? stripHtmlTags(task.last_comment_message) : '') || emptyTableCellCopy;
  const isClosed = task.status === 'completed';

  const content = (
    <div className="flex items-start justify-between gap-2">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-baseline gap-1">
          <Typography
            styledAs="bodySmallDMSans"
            renderedAs="div"
            weight={600}
            color="var(--grayscale-900)"
            className="max-w-[250px] shrink-0"
          >
            <TruncatedText lines={1} tooltipPlacement="bottom">
              {task.task_type}
            </TruncatedText>
          </Typography>
          <Typography styledAs="bodySmallDMSans" renderedAs="span" color="var(--grayscale-600)" aria-hidden>
            |
          </Typography>
          <Typography
            styledAs="bodySmallDMSans"
            renderedAs="div"
            color="var(--grayscale-600)"
            className="min-w-0 flex-1"
          >
            <TruncatedText lines={1} tooltipPlacement="bottom">
              {task.drug?.drug_name ?? ''}
            </TruncatedText>
          </Typography>
        </div>
        <Typography styledAs="bodyExtraSmallDMSans" renderedAs="div" color="var(--grayscale-900)">
          <TruncatedText lines={1} tooltipPlacement="bottom">
            {messageText}
          </TruncatedText>
        </Typography>
        <div className="flex items-baseline gap-0.5">
          <Typography
            styledAs="bodyExtraSmallDMSans"
            renderedAs="div"
            weight={500}
            color="var(--grayscale-900)"
            className="max-w-[200px] shrink-0"
          >
            <SearchHighlightText searchTerm={searchTerm} value={task.patient_name} fieldType="name" truncate />
          </Typography>
          {task.patient_dob && (
            <Typography styledAs="bodyExtraSmallDMSans" renderedAs="span" color="var(--grayscale-600)">
              (DOB: <SearchHighlightText searchTerm={searchTerm} value={task.patient_dob} fieldType="date" />)
            </Typography>
          )}
        </div>
      </div>

      <PreviewDateWithStatus date={task.last_comment_created_at} statusLabel={isClosed ? 'Closed' : undefined} />
    </div>
  );

  return content;
};

const taskDateSortingFn = (a: Row<TaskSummary>, b: Row<TaskSummary>, columnId: string) => {
  return new Date(b.getValue(columnId)).getTime() - new Date(a.getValue(columnId)).getTime();
};

const taskPatientSortingFn = (a: Row<TaskSummary>, b: Row<TaskSummary>, columnId: string) => {
  const aValue = a.getValue(columnId) as { name: string; dob: string } | null;
  const bValue = b.getValue(columnId) as { name: string; dob: string } | null;
  if (aValue === null && bValue === null) {
    return 0;
  }
  if (aValue === null) {
    return 1;
  }
  if (bValue === null) {
    return -1;
  }
  return bValue.name.localeCompare(aValue.name);
};

const taskTypeSortingFn = (a: Row<TaskSummary>, b: Row<TaskSummary>, columnId: string) => {
  const aValue = a.getValue(columnId) as { type: string; drugDescription: string; message: string };
  const bValue = b.getValue(columnId) as { type: string; drugDescription: string; message: string };
  return bValue.type.localeCompare(aValue.type);
};

const taskTableMultiSelectFilter = (row: Row<TaskSummary>, columnId: string, filterValue: string[]) => {
  if (!filterValue.length) {
    // No filters are applied; return all rows
    return true;
  }

  // @ts-expect-error columnId will be one of the column heading keys
  return filterValue.includes(row.original[columnId]);
};

// drug info columns need a special filter function because they're in a nested
// object
const drugInfoMultiSelectFilter = (
  row: Row<TaskSummary>,
  columnId: 'prescriber_name' | 'drug_category',
  filterValue: string[],
) => {
  if (!filterValue.length) {
    // No filters are applied; return all rows
    return true;
  }

  return filterValue.includes(row.original.drug?.[columnId] || '');
};

type DateRangeFilterValue = {
  startDate?: string;
  endDate?: string;
};

const parseDateBoundary = (value: string | undefined, boundary: 'start' | 'end') => {
  if (!value) {
    return null;
  }

  const parsed = moment(value, moment.ISO_8601, true);
  if (!parsed.isValid()) {
    return null;
  }

  return boundary === 'start' ? parsed.clone().startOf('day') : parsed.clone().endOf('day');
};

const isWithinDateRange = (dateString: string, { startDate, endDate }: DateRangeFilterValue) => {
  if (!startDate && !endDate) {
    return true;
  }

  const date = moment(dateString);
  if (!date.isValid()) {
    return true;
  }

  const start = parseDateBoundary(startDate, 'start');
  if (start && date.isBefore(start)) {
    return false;
  }

  const end = parseDateBoundary(endDate, 'end');
  if (end && date.isAfter(end)) {
    return false;
  }

  return true;
};

const getDefaultNullStateType = (status: PartnerTaskStatusFilter) => {
  if (status === PartnerTaskStatusFilter.SENT) {
    return TableNullStateType.NO_TASKS_SENT;
  } else if (status === PartnerTaskStatusFilter.COMPLETED) {
    return TableNullStateType.NO_TASKS_COMPLETED;
  } else {
    return TableNullStateType.NO_TASKS_OPEN;
  }
};

/**
 * Component that shows submit appeal button when row is active (hovered or focused), with optional fallback content
 */
const HoverSubmitAppealButton: React.FC<{
  task: TaskSummary;
  setSubmitAppealModalTask: (task: TaskSummary) => void;
}> = ({ task, setSubmitAppealModalTask }) => {
  return (
    <Button
      size="sm"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        setSubmitAppealModalTask(task);
      }}
      // Stop propagation for keyboard activation (browser will synthesize a click that triggers onClick)
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
        }
      }}
      {...createTrackingProps({ elementName: 'Tasks Table: Submit Appeal Button', unmask: true })}
    >
      Submit appeal
    </Button>
  );
};

/**
 * Builds overlay content for a task cell
 * @param task - The task summary
 * @param setSubmitAppealModalTask - Callback to open the submit appeal modal
 * @param isRowActive - Whether the row is currently hovered or focused
 * @returns React node for the overlay content or undefined if no overlay should be shown
 */
const getTaskCellOverlayContent = (
  task: TaskSummary,
  setSubmitAppealModalTask: (task: TaskSummary) => void,
  isRowActive: boolean,
): React.ReactNode => {
  const expirationMetadata = task.denial_pred_expiration_metadata;
  const showPillStatus = Boolean(expirationMetadata && !expirationMetadata.is_held && task.status !== 'completed');
  const pillStatusText = expirationMetadata ? formatRemainingTime(expirationMetadata.expired_at) : '';

  const showSubmitAppealButton =
    task.task_key === OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS_AND_APPEAL_REVIEW &&
    task.status !== 'completed' &&
    isRowActive;

  if (showSubmitAppealButton) {
    return <HoverSubmitAppealButton task={task} setSubmitAppealModalTask={setSubmitAppealModalTask} />;
  }

  if (showPillStatus) {
    return <PillStatus variant="info">{pillStatusText}</PillStatus>;
  }

  return undefined;
};

// Map task status to user-friendly labels that match the tab names
const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'open':
      return 'Requires response';
    case 'sent':
      return 'Awaiting Tandem';
    case 'completed':
      return 'Closed';
    default:
      return status;
  }
};

const TasksTable: React.FC<TaskTableProps> = ({ data, isLoading = false, error = null }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const authContext = React.useContext(AuthContext);
  const userEmail = authContext.user?.email;
  const tasksTabId = useId();
  const tasksPanelId = useId();

  const { data: filterOptions } = useFetchPortalTableFilters();

  // Add search value state for controlled input
  const [searchValue, setSearchValue] = useState(searchParams.get('search') || '');
  // Applied search is what's actually filtering the table (only updated on Enter)
  const [appliedSearch, setAppliedSearch] = useState(searchParams.get('search') || '');
  // Store search results when search is applied (Enter pressed) so table doesn't change while typing more.
  // We can't rely solely on react-query's placeholderData because useSearchTasks queries using searchValue
  // (what's being typed), not appliedSearch (what was submitted). When the user types more after pressing
  // Enter, searchResults updates for the new query but the table should keep showing the applied results.
  const [appliedSearchResults, setAppliedSearchResults] = useState<TaskSummary[]>([]);

  // Fetch search results from the server for the search preview dropdown
  // Note: searchValue is already debounced by TableSearchWithPreview (300ms)
  const { data: searchResults, isFetching: isSearching } = useSearchTasks(searchValue);

  // When search is applied, use backend search results; otherwise use current tab's data
  const tableData = appliedSearch ? appliedSearchResults : data;

  const lastCommentStartParam = searchParams.get('last_comment_created_at_start') || '';
  const lastCommentEndParam = searchParams.get('last_comment_created_at_end') || '';

  // Refs for TableMultiselectFilter components
  const officeFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const prescriberFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const typeFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const drugFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const drugCategoryFilterRef = useRef<MultiselectAutocompleteHandle>(null);

  const { defaultPrescriberNames, defaultPrescriberNpis } = useDefaultFilters();
  const officeCookieValues = React.useMemo(
    () => getInitialTableColumnFilterValueFromCookie(authContext.user?.member_id || null, 'office_name'),
    [authContext.user?.member_id],
  );
  const drugCookieValues = React.useMemo(
    () => getInitialTableColumnFilterValueFromCookie(authContext.user?.member_id || null, 'drug_name'),
    [authContext.user?.member_id],
  );
  const drugCategoryCookieValues = React.useMemo(
    () => getInitialTableColumnFilterValueFromCookie(authContext.user?.member_id || null, 'drug_category'),
    [authContext.user?.member_id],
  );
  const hasUserClearedFilters = React.useRef(false);

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const emptyFilters = React.useMemo<ColumnFiltersState>(() => [], []);

  const currentPrescriberFilter = (columnFilters.find((f) => f.id === 'prescriber_name')?.value as string[]) || [];
  const currentPrescriberFilterNpis = currentPrescriberFilter
    .map((name) => {
      const prescriber = filterOptions?.prescribers?.find((p) => p.description === name);
      return prescriber?.value;
    })
    .filter((npi): npi is string => npi !== undefined);
  const showSaveButton = useMemo(
    () =>
      filterOptions?.prescribers ? !areStringArraysEqual(currentPrescriberFilterNpis, defaultPrescriberNpis) : false,
    [filterOptions?.prescribers, currentPrescriberFilterNpis, defaultPrescriberNpis],
  );

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [submitAppealModalTask, setSubmitAppealModalTask] = useState<TaskSummary | null>(null);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);

  const typeStrings: string[] = [];
  data.forEach((task) => {
    if (task.task_type) {
      typeStrings.push(task.task_type);
    }
  });

  const columns = [
    columnHelper.accessor('last_comment_created_at', {
      header: 'Last updated',
      sortingFn: taskDateSortingFn,
      size: DATE_COLUMN_SIZE,
      cell: (info) => (
        <DateCellWithStatus
          date={info.getValue()}
          statusLabel={appliedSearch && info.row.original.status === 'completed' ? 'Closed' : undefined}
        />
      ),
      filterFn: (row, _, filterValue) => {
        if (!filterValue || typeof filterValue !== 'object') {
          return true;
        }

        return isWithinDateRange(row.original.last_comment_created_at, filterValue as DateRangeFilterValue);
      },
    }),
    columnHelper.accessor(
      (row) => {
        if (row.patient_name) {
          return { name: row.patient_name, dob: row.patient_dob! };
        }
        return null;
      },
      {
        id: 'patient_info',
        header: 'Patient info',
        sortingFn: taskPatientSortingFn,
        cell: (info) => {
          const value = info.getValue();
          if (value === null) {
            return <FormattedCell id={info.row.original.id} title="N/A" size={DEFAULT_COLUMN_SIZE} />;
          } else {
            return (
              <FormattedCell
                id={info.row.original.id}
                title={value.name}
                subtitle={`DOB: ${value.dob}`}
                size={DEFAULT_COLUMN_SIZE}
              />
            );
          }
        },
      },
    ),
    columnHelper.accessor(
      (row) => {
        return { type: row.task_type, message: row.last_comment_message, drugDescription: row.drug?.drug_name };
      },
      {
        id: 'task',
        header: 'Task',
        sortingFn: taskTypeSortingFn,
        cell: (info) => {
          const attachmentsCount = info.row.original.last_comment_attachments_count;
          const attachmentText = attachmentsCount
            ? ` [uploaded ${attachmentsCount} document${attachmentsCount > 1 ? 's' : ''}]`
            : '';
          const messageText = `${info.getValue().message ? stripHtmlTags(info.getValue().message) : ''}${attachmentText}`;

          const isRowActive = activeTaskId === info.row.original.id;
          const overlayContent = getTaskCellOverlayContent(info.row.original, setSubmitAppealModalTask, isRowActive);

          return (
            <FormattedCell
              id={info.row.original.id}
              title={info.getValue().type}
              subtitle={info.getValue().drugDescription}
              body={messageText}
              truncateBody
              overlayContent={overlayContent}
            />
          );
        },
        filterFn: (row, _, filterValue) => {
          if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
          return filterValue.includes(row.original.task_type);
        },
        size: 600,
      },
    ),
    columnHelper.accessor((row) => row.drug?.prescriber_name || '', {
      id: 'prescriber_name',
      header: 'Prescriber',
      filterFn: (row, _, filterValue) => drugInfoMultiSelectFilter(row, 'prescriber_name', filterValue),
      size: DEFAULT_COLUMN_SIZE,
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => {
        return <Typography styledAs="bodySmallDMSans">{getStatusLabel(info.getValue())}</Typography>;
      },
      size: DEFAULT_COLUMN_SIZE,
    }),

    // these columns are only used for filtering, not displaying
    columnHelper.accessor('task_type', {
      enableGlobalFilter: false,
      filterFn: taskTableMultiSelectFilter,
    }),
    columnHelper.accessor((row) => row.drug?.drug_category || '', {
      id: 'drug_category',
      header: 'Drug category',
      filterFn: (row, _, filterValue) => drugInfoMultiSelectFilter(row, 'drug_category', filterValue),
      enableGlobalFilter: false,
    }),
    // drug_name is included in the 'task' column, but it's easier to also give it its own column
    // for filtering purposes
    columnHelper.accessor((row) => row.drug?.drug_name || '', {
      id: 'drug_name',
      header: 'Drug',
      enableGlobalFilter: false,
      filterFn: (row, _, filterValue) => {
        if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
        // drug_description requires special handling because we're looking for a substring match
        // whereas the other columns are looking for an exact match
        const drugName = row.original.drug?.drug_name?.toLowerCase() || '';
        return filterValue.some((filterVal) => drugName.includes(filterVal.toLowerCase()));
      },
    }),
    columnHelper.accessor('office_name', {
      header: 'Office',
      enableGlobalFilter: false,
      filterFn: taskTableMultiSelectFilter,
    }),
  ];

  // Status column is always hidden (closed items show indicator in date column instead)
  const columnVisibility = React.useMemo(
    () => ({
      ...HIDDEN_FILTER_COLUMNS,
      status: false,
    }),
    [],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      // When search is applied, don't apply column filters (backend already filtered the results)
      columnFilters: appliedSearch ? emptyFilters : columnFilters,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
  });

  React.useEffect(() => {
    const searchFromParams = searchParams.get('search') || '';
    setSearchValue(searchFromParams);
    setAppliedSearch(searchFromParams);
    // Note: appliedSearchResults will be populated by the useSearchTasks effect below

    const filterValues = tasksFilterValuesFromSearchParams(searchParams);

    const prescriberValue = getPrescriberFilterValue(
      filterValues,
      filterOptions,
      defaultPrescriberNames,
      hasUserClearedFilters,
    );

    setColumnFilters([
      {
        id: 'office_name',
        value: filterValues.office.length
          ? filterValues.office
          : hasUserClearedFilters.current
            ? []
            : officeCookieValues,
      },
      {
        id: 'prescriber_name',
        value: prescriberValue,
      },
      { id: 'task_type', value: filterValues.type },
      {
        id: 'drug_category',
        value: filterValues.drug_category.length
          ? filterValues.drug_category
          : hasUserClearedFilters.current
            ? []
            : drugCategoryCookieValues,
      },
      {
        id: 'drug_name',
        value: filterValues.drug_name.length
          ? filterValues.drug_name
          : hasUserClearedFilters.current
            ? []
            : drugCookieValues,
      },
      {
        id: 'last_comment_created_at',
        value: {
          startDate: filterValues.startDate || undefined,
          endDate: filterValues.endDate || undefined,
        },
      },
    ]);
  }, [
    searchParams,
    userEmail,
    defaultPrescriberNames,
    filterOptions,
    officeCookieValues,
    drugCookieValues,
    drugCategoryCookieValues,
  ]);

  // Populate appliedSearchResults when search results arrive for the applied search
  // This handles both initial page load with search param and subsequent searches
  React.useEffect(() => {
    if (appliedSearch && searchResults?.tasks && searchValue === appliedSearch) {
      setAppliedSearchResults(searchResults.tasks);
    }
  }, [appliedSearch, searchResults, searchValue]);

  const setStatus = React.useCallback(
    (status: string) => {
      updateUrlSearchParams(searchParams, setSearchParams, { status });
    },
    [searchParams, setSearchParams],
  );

  // Get current status from URL for UI state with validation
  const statusParam = searchParams.get('status');
  const currentStatus =
    statusParam === 'open' || statusParam === 'sent' || statusParam === 'completed'
      ? statusParam
      : PartnerTaskStatusFilter.OPEN;

  // Render table body content based on state
  const renderTableBody = () => {
    if (error) {
      return (
        <tr>
          <TableCell colSpan={columns.length}>
            <PageLoadError />
          </TableCell>
        </tr>
      );
    }

    // TODO: also show loading spinner if we're fetching default filters to avoid flickering
    if (isLoading) {
      return (
        <tr>
          <CenteredTableCell colSpan={columns.length}>
            <LoadingSpinner />
          </CenteredTableCell>
        </tr>
      );
    }

    if (table.getRowModel().rows?.length) {
      return table.getRowModel().rows.map((row) => (
        <ClickableRow
          key={row.original.id}
          onClick={() => navigate(row.original.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              navigate(row.original.id);
            }
          }}
          onMouseEnter={() => setActiveTaskId(row.original.id)}
          onMouseLeave={() => setActiveTaskId(null)}
          onFocus={() => setActiveTaskId(row.original.id)}
          onBlur={(e) => {
            // Only clear active state if focus is leaving the row entirely
            // (not moving to a child element like the button)
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setActiveTaskId(null);
            }
          }}
        >
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id} $width={`${cell.column.getSize()}px`}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </ClickableRow>
      ));
    }

    return (
      <tr>
        <TableCell colSpan={columns.length}>
          <TableNullState
            nullStateType={nullStateType}
            onClearFilters={clearAllFilters}
            onClearSearch={clearSearch}
            onAction={() => navigate('/settings?tab=notifications')}
            tableName="Tasks Table"
          />
        </TableCell>
      </tr>
    );
  };

  const nullStateType = useTableNullState({
    columnFilters,
    filterIds: ['office_name', 'prescriber_name', 'task_type', 'drug_category', 'drug_name', 'last_comment_created_at'],
    defaultNullStateType: getDefaultNullStateType(currentStatus as PartnerTaskStatusFilter),
    globalFilter: appliedSearch,
  });

  // Clear search input and results
  const clearSearch = React.useCallback(() => {
    setSearchValue('');
    setAppliedSearch('');
    setAppliedSearchResults([]);
    updateUrlSearchParams(searchParams, setSearchParams, { search: undefined });
  }, [searchParams, setSearchParams]);

  const clearAllFilters = React.useCallback(() => {
    // Clear all column filters except status (which is controlled by URL)
    const statusParam = searchParams.get('status');
    const currentStatus =
      statusParam === 'open' || statusParam === 'sent' || statusParam === 'completed'
        ? statusParam
        : PartnerTaskStatusFilter.OPEN;
    setColumnFilters([
      { id: 'status', value: currentStatus },
      {
        id: 'last_comment_created_at',
        value: { startDate: undefined, endDate: undefined },
      },
    ]);

    table.getColumn('last_comment_created_at')?.setFilterValue(undefined);

    // Clear cookies for saved filter values
    updateTableColumnFilterValueCookie([], authContext.user?.member_id || null, 'office_name');
    updateTableColumnFilterValueCookie([], authContext.user?.member_id || null, 'drug_name');
    updateTableColumnFilterValueCookie([], authContext.user?.member_id || null, 'drug_category');

    // Clear TableMultiselectFilter components
    officeFilterRef.current?.clear();
    prescriberFilterRef.current?.clear();
    typeFilterRef.current?.clear();
    drugCategoryFilterRef.current?.clear();
    drugFilterRef.current?.clear();

    // Mark that user has explicitly cleared filters
    hasUserClearedFilters.current = true;

    updateUrlSearchParams(searchParams, setSearchParams, {
      search: undefined,
      office: undefined,
      prescriber: undefined,
      type: undefined,
      drug_category: undefined,
      drug_name: undefined,
      last_comment_created_at_start: undefined,
      last_comment_created_at_end: undefined,
    });

    // TODO: after clearing the filters, we should set the focus state on another element on the page
    // because the focus is currently on the clear filters button, which will disappear after the
    // filters are cleared
  }, [table, searchParams, setSearchParams, authContext.user?.member_id]);

  return (
    <TableContainer>
      <FilterBar $mobileBreakpoint={TASKS_TABLE_MOBILE_BREAKPOINT}>
        <TabsAndSearchRow $mobileBreakpoint={TASKS_TABLE_MOBILE_BREAKPOINT}>
          <TableTabList
            tabs={[
              {
                label: 'Requires response',
                value: 'open',
              },
              {
                label: 'Awaiting Tandem',
                value: 'sent',
              },
              {
                label: 'Closed',
                value: 'completed',
              },
            ]}
            selectedValue={appliedSearch ? '' : currentStatus}
            onSelect={(status) => {
              if (appliedSearch) {
                // Clear search when selecting a tab while search is active
                setSearchValue('');
                setAppliedSearch('');
                setAppliedSearchResults([]);
                updateUrlSearchParams(searchParams, setSearchParams, {
                  search: undefined,
                  status,
                });
              } else {
                setStatus(status);
              }
            }}
            tabPanelId={tasksPanelId}
            tabListId={tasksTabId}
            ariaLabel="Filter tasks by status"
            {...createTrackingProps({ elementName: 'Tasks Table: Status Tabs', unmask: true })}
          />
          <TableSearchWithPreview
            tableName="Tasks Table"
            value={searchValue}
            onChange={setSearchValue}
            onApplySearch={(value) => {
              setAppliedSearch(value);
              // Use current search results if available
              // Only clear results when value is empty; if searchResults is temporarily unavailable, keep existing
              if (value) {
                if (searchResults?.tasks) {
                  setAppliedSearchResults(searchResults.tasks);
                }
                // If searchResults is unavailable, keep existing appliedSearchResults
              } else {
                setAppliedSearchResults([]);
              }
              updateUrlSearchParams(searchParams, setSearchParams, {
                search: value === '' ? undefined : value,
              });
            }}
            onClear={() => {
              setAppliedSearch('');
              setAppliedSearchResults([]);
              updateUrlSearchParams(searchParams, setSearchParams, {
                search: undefined,
              });
            }}
            data={searchResults?.tasks || []}
            getItemKey={(task) => task.id}
            onPreviewItemClick={(task) => navigate(task.id)}
            renderPreviewItem={(task) => <TaskPreviewItem task={task} searchTerm={searchValue} />}
            mobileBreakpoint={TASKS_TABLE_MOBILE_BREAKPOINT}
            isLoading={isSearching}
            minSearchLength={MIN_SEARCH_LENGTH}
          />
        </TabsAndSearchRow>

        {appliedSearch ? (
          <SearchResultsBanner
            searchTerm={appliedSearch}
            onClearSearch={clearSearch}
            count={table.getRowModel().rows?.length ?? 0}
          />
        ) : (
          <FilterBarRow>
            <TableMultiselectFilter
              ref={officeFilterRef}
              options={filterOptions?.offices || []}
              label={'Office'}
              initialValue={
                getDropdownInitialValueFromSearchParamsAndCookie(
                  searchParams.get('office') ||
                    (hasUserClearedFilters.current ? '' : officeCookieValues.join(MULTI_SELECT_DELINEATOR)),
                  authContext.user?.member_id || null,
                  'office_name',
                  filterOptions?.offices,
                ) as MultiselectAutocompleteOption[]
              }
              onChange={(_, value: MultiselectAutocompleteOption[]) => {
                const selectedValuesList = value.map((valueItem) => valueItem.value);

                // Reset the cleared flag when user makes a manual selection
                hasUserClearedFilters.current = false;

                table.getColumn('office_name')!.setFilterValue(selectedValuesList);
                updateTableColumnFilterValueCookie(value, authContext.user?.member_id || null, 'office_name');
                updateUrlSearchParams(searchParams, setSearchParams, {
                  office: selectedValuesList.length ? selectedValuesList.join(MULTI_SELECT_DELINEATOR) : undefined,
                });
              }}
              sortFn={sortOfficeNames}
              {...createTrackingProps({ elementName: 'Tasks Table: Office Filter', unmask: true })}
            />
            <TableMultiselectFilter
              // Force remount when prescriber options load to ensure initialValue is applied correctly
              key={filterOptions?.prescribers ? 'prescriber-loaded' : 'prescriber-loading'}
              ref={prescriberFilterRef}
              options={filterOptions?.prescribers || []}
              label={'Prescriber'}
              initialValue={
                filterOptions?.prescribers
                  ? (getDropdownInitialValueFromSearchParams(
                      authContext.user?.default_filters,
                      searchParams.get('prescriber') || '',
                      'providers',
                      filterOptions.prescribers,
                    ) as MultiselectAutocompleteOption[])
                  : []
              }
              onChange={(_, value: MultiselectAutocompleteOption[]) => {
                const selectedNamesList = value.map((valueItem) => valueItem.description);
                const selectedNpisList = value.map((valueItem) => valueItem.value);

                // Reset the cleared flag when user makes a manual selection
                hasUserClearedFilters.current = false;

                table.getColumn('prescriber_name')!.setFilterValue(selectedNamesList);
                updateUrlSearchParams(searchParams, setSearchParams, {
                  prescriber: selectedNpisList.length ? selectedNpisList.join(MULTI_SELECT_DELINEATOR) : undefined,
                });
              }}
              {...createTrackingProps({ elementName: 'Tasks Table: Prescriber Filter', unmask: true })}
            />
            <TableMultiselectFilter
              ref={drugFilterRef}
              options={filterOptions?.drugs || []}
              label={'Drug'}
              initialValue={
                getDropdownInitialValueFromSearchParamsAndCookie(
                  searchParams.get('drug_name') ||
                    (hasUserClearedFilters.current ? '' : drugCookieValues.join(MULTI_SELECT_DELINEATOR)),
                  authContext.user?.member_id || null,
                  'drug_name',
                  filterOptions?.drugs,
                ) as MultiselectAutocompleteOption[]
              }
              onChange={(_, value: MultiselectAutocompleteOption[]) => {
                const selectedValuesList = value.map((valueItem) => valueItem.value);
                hasUserClearedFilters.current = false;
                table.getColumn('drug_name')!.setFilterValue(selectedValuesList);
                updateTableColumnFilterValueCookie(value, authContext.user?.member_id || null, 'drug_name');
                updateUrlSearchParams(searchParams, setSearchParams, {
                  drug_name: selectedValuesList.length ? selectedValuesList.join(MULTI_SELECT_DELINEATOR) : undefined,
                });
              }}
              {...createTrackingProps({ elementName: 'Tasks Table: Drug Filter', unmask: true })}
            />
            <TableMultiselectFilter
              ref={drugCategoryFilterRef}
              options={DRUG_CATEGORY_DROPDOWN_OPTIONS}
              label={'Drug category'}
              initialValue={
                getDropdownInitialValueFromSearchParamsAndCookie(
                  searchParams.get('drug_category') ||
                    (hasUserClearedFilters.current ? '' : drugCategoryCookieValues.join(MULTI_SELECT_DELINEATOR)),
                  authContext.user?.member_id || null,
                  'drug_category',
                  DRUG_CATEGORY_DROPDOWN_OPTIONS,
                ) as MultiselectAutocompleteOption[]
              }
              onChange={(_, value: MultiselectAutocompleteOption[]) => {
                const selectedValuesList = value.map((valueItem) => valueItem.value);
                hasUserClearedFilters.current = false;
                table.getColumn('drug_category')!.setFilterValue(selectedValuesList);
                updateTableColumnFilterValueCookie(value, authContext.user?.member_id || null, 'drug_category');
                updateUrlSearchParams(searchParams, setSearchParams, {
                  drug_category: selectedValuesList.length
                    ? selectedValuesList.join(MULTI_SELECT_DELINEATOR)
                    : undefined,
                });
              }}
              {...createTrackingProps({ elementName: 'Tasks Table: Drug Category Filter', unmask: true })}
            />
            <TableMultiselectFilter
              ref={typeFilterRef}
              options={stringsToDropdownOptions(typeStrings)}
              label={'Type'}
              initialValue={
                getDropdownInitialValueFromSearchParams(
                  null,
                  searchParams.get('type') || '',
                  undefined,
                  stringsToDropdownOptions(typeStrings),
                ) as MultiselectAutocompleteOption[]
              }
              onChange={(_, value: MultiselectAutocompleteOption[]) => {
                const selectedValuesList = value.map((valueItem) => valueItem.value);
                table.getColumn('task_type')!.setFilterValue(selectedValuesList);
                updateUrlSearchParams(searchParams, setSearchParams, {
                  type: selectedValuesList.length ? selectedValuesList.join(MULTI_SELECT_DELINEATOR) : undefined,
                });
              }}
              {...createTrackingProps({ elementName: 'Tasks Table: Type Filter', unmask: true })}
            />
            <DateFilter
              label="Last updated"
              startDate={lastCommentStartParam || undefined}
              endDate={lastCommentEndParam || undefined}
              onApply={({ startDate, endDate }) => {
                const normalizedStartDate = startDate || undefined;
                const normalizedEndDate = endDate || undefined;

                if (normalizedStartDate || normalizedEndDate) {
                  table.getColumn('last_comment_created_at')?.setFilterValue({
                    startDate: normalizedStartDate,
                    endDate: normalizedEndDate,
                  });
                } else {
                  table.getColumn('last_comment_created_at')?.setFilterValue(undefined);
                }

                updateUrlSearchParams(searchParams, setSearchParams, {
                  last_comment_created_at_start: normalizedStartDate,
                  last_comment_created_at_end: normalizedEndDate,
                });
              }}
              onClear={() => {
                table.getColumn('last_comment_created_at')?.setFilterValue(undefined);
                updateUrlSearchParams(searchParams, setSearchParams, {
                  last_comment_created_at_start: undefined,
                  last_comment_created_at_end: undefined,
                });
              }}
              {...createTrackingProps({ elementName: 'Tasks Table: Date Filter', unmask: true })}
            />
            <SaveFiltersButton
              $visible={showSaveButton}
              onClick={() => setShowSaveModal(true)}
              {...createTrackingProps({ elementName: 'Tasks Table: Save Filters Button' })}
            />
            <TableResultCount count={table.getRowModel().rows?.length ?? 0} />
          </FilterBarRow>
        )}
      </FilterBar>

      <Table role="tabpanel" id={tasksPanelId} aria-labelledby={tasksTabId}>
        <thead {...createTrackingProps({ unmask: true })}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHeader
                    key={header.id}
                    onClick={header.column.getCanSort() ? () => header.column.toggleSorting() : undefined}
                    $sortable={header.column.getCanSort()}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <HeaderSortingIndicator
                        isSorted={header.column.getIsSorted()}
                        sortDirection={header.column.getIsSorted()}
                      />
                    )}
                  </TableHeader>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>{renderTableBody()}</tbody>
      </Table>
      {showSaveModal && (
        <SaveFiltersModal
          onClose={() => setShowSaveModal(false)}
          filters={[
            { label: 'Prescriber', displayValues: currentPrescriberFilter, saveValues: currentPrescriberFilterNpis },
          ]}
        />
      )}
      {submitAppealModalTask && (
        <SubmitAppealModal
          closeModal={() => setSubmitAppealModalTask(null)}
          patient={
            {
              first_name: submitAppealModalTask.patient_name?.split(', ')[1] || '',
              last_name: submitAppealModalTask.patient_name?.split(', ')[0] || '',
            } as Patient
          }
          drug={{ name: submitAppealModalTask.drug?.drug_name } as Drug}
          taskId={submitAppealModalTask.id}
          includeEnrollmentNotice={
            submitAppealModalTask.task_key === OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS_AND_APPEAL_REVIEW
          }
        />
      )}
    </TableContainer>
  );
};

export default TasksTable;
